import { resolveId } from "@medplum/core";
import type { Observation, Reference } from "@medplum/fhirtypes";

import { observationHasSharedPostpartumRelatedCode } from "@/encounters/components/EncounterLabor/constants/shared-postpartum-related-observation-codes";
import type { LaborStage } from "@/encounters/components/EncounterLabor/types/labor";
import { dedupeLaborStagesPreservingAmendPreviews } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview-labor-stages";
import { getLaborStageAnswerCode } from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import { mergeIncomingObservationsIntoCache } from "@/encounters/components/EncounterLabor/utils/postpartum-observation-processing";
import type { RefCell } from "@/encounters/components/EncounterLabor/utils/postpartum-observation-processing";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { BirthingComponent, QuiltedHealthCodes } from "@/fhir/enums/quilted-health";
import type { AppDispatch, RootState } from "@/store";
import { clearBirthState, selectCurrentBirth, setBirthObservations } from "@/store/slices/birth";
import { loadLaborStages, selectLaborStageEntities } from "@/store/slices/labor-stages";
import { selectPostpartumObservations } from "@/store/slices/postpartum-observations";

export function observationMetaTimeMs(observation: Observation): number {
  const metaLastUpdatedTimeMs = new Date(observation.meta?.lastUpdated || 0).getTime();
  return Number.isNaN(metaLastUpdatedTimeMs) ? 0 : metaLastUpdatedTimeMs;
}

function observationSubjectId(observation: Observation): string | undefined {
  return resolveId(observation.subject as Reference);
}

function isBirthObservation(observation: Observation): boolean {
  return (
    observation.code?.coding?.some(
      (codingEntry) => codingEntry.code === QuiltedHealthCodes.BirthDateTime,
    ) ||
    Boolean(
      observation.component?.some((componentEntry) =>
        componentEntry.code?.coding?.some(
          (componentCoding) => componentCoding.code === BirthingComponent.BodyDeliveredDateTime,
        ),
      ),
    )
  );
}

function shouldReplaceBirthObservation(
  currentBirthObservation: Observation | undefined,
  nextBirthObservation: Observation,
): boolean {
  if (!currentBirthObservation) {
    return true;
  }

  const currentMetaTime = observationMetaTimeMs(currentBirthObservation);
  const nextMetaTime = observationMetaTimeMs(nextBirthObservation);

  if (nextMetaTime > currentMetaTime) {
    return true;
  }

  if (nextMetaTime < currentMetaTime) {
    return false;
  }

  const currentVersionId = Number(currentBirthObservation.meta?.versionId ?? 0);
  const nextVersionId = Number(nextBirthObservation.meta?.versionId ?? 0);
  if (
    !Number.isNaN(currentVersionId) &&
    !Number.isNaN(nextVersionId) &&
    nextVersionId > currentVersionId
  ) {
    return true;
  }

  return false;
}

function shouldClearBirthObservation(
  currentBirthObservation: Observation | undefined,
  cancelledBirthObservation: Observation,
): boolean {
  if (!currentBirthObservation?.id || currentBirthObservation.id !== cancelledBirthObservation.id) {
    return false;
  }

  const cancelledMetaTime = observationMetaTimeMs(cancelledBirthObservation);
  if (cancelledMetaTime <= 0) {
    // Without a usable `meta.lastUpdated`, do not clear birth state (avoids false clears on bad meta).
    return false;
  }

  return shouldReplaceBirthObservation(currentBirthObservation, cancelledBirthObservation);
}

export function isMotherCumulativeBloodLossObservation(
  observation: Observation,
  clientId: string,
): boolean {
  if (observationSubjectId(observation) !== clientId) {
    return false;
  }
  return Boolean(
    observation.code?.coding?.some(
      (codingEntry) => codingEntry.code === LOINCCodes.CumulativeBloodLoss,
    ),
  );
}

export function applyMotherCumulativeBloodLossIfNewer(
  latest: Observation | undefined,
  motherCblLastAppliedMetaMsRef: RefCell<number>,
  setLiveCumulativeBloodLoss: (value: number) => void,
) {
  if (!latest) return;
  const latestMetaLastUpdatedTimeMs = observationMetaTimeMs(latest);
  const cumulativeBloodLossQuantityValue = latest.valueQuantity?.value;
  if (cumulativeBloodLossQuantityValue === undefined) return;
  // Ignore rows without a usable `meta.lastUpdated` for ordering (invalid dates sort as 0).
  if (latestMetaLastUpdatedTimeMs <= 0) {
    return;
  }
  if (latestMetaLastUpdatedTimeMs < motherCblLastAppliedMetaMsRef.current) {
    return;
  }
  motherCblLastAppliedMetaMsRef.current = Math.max(
    motherCblLastAppliedMetaMsRef.current,
    latestMetaLastUpdatedTimeMs,
  );
  setLiveCumulativeBloodLoss(cumulativeBloodLossQuantityValue);
}

function applyBirthObservationUpdatesFromBundle(
  resources: Observation[],
  clientId: string,
  getState: () => RootState,
  dispatch: AppDispatch,
): void {
  const birthSubjectMatches = resources.filter(
    (observation) =>
      isBirthObservation(observation) && observationSubjectId(observation) === clientId,
  );
  const activeBirthMatches = birthSubjectMatches.filter(
    (observation) => observation.status !== "cancelled",
  );
  const currentBirthObservation = selectCurrentBirth(getState());
  if (activeBirthMatches.length > 0) {
    const sortedBirth = [...activeBirthMatches].sort(
      (a, b) => observationMetaTimeMs(b) - observationMetaTimeMs(a),
    );
    if (shouldReplaceBirthObservation(currentBirthObservation, sortedBirth[0])) {
      dispatch(setBirthObservations(sortedBirth));
    }
  } else if (birthSubjectMatches.length > 0) {
    const hasCancelledCurrentBirth = birthSubjectMatches.some(
      (observation) =>
        observation.status === "cancelled" &&
        shouldClearBirthObservation(currentBirthObservation, observation),
    );
    if (hasCancelledCurrentBirth) {
      dispatch(clearBirthState());
    }
  }
}

async function mergePostpartumObservationsFromLiveBundle(
  resources: Observation[],
  asyncSeq: number,
  clientId: string,
  relatedPatientId: string | null,
  getState: () => RootState,
  observationsCacheRef: RefCell<Map<string, Observation>>,
  processObservations: (observations: Observation[], asyncSeq: number) => Promise<void>,
  bundleAsyncSeqRef: RefCell<number>,
): Promise<void> {
  // `relatedPatientId` resolves async; HTTP refresh after it settles usually covers bundles that
  // arrived earlier for the newborn subject.
  const postpartumIncoming = resources.filter((observation) => {
    const subjectId = observationSubjectId(observation);
    if (subjectId === clientId) return true;
    if (relatedPatientId && subjectId === relatedPatientId) {
      return observationHasSharedPostpartumRelatedCode(observation);
    }
    return false;
  });

  if (postpartumIncoming.length === 0) {
    return;
  }
  if (asyncSeq !== bundleAsyncSeqRef.current) {
    return;
  }
  const reduxEntities = selectPostpartumObservations(getState());
  const allObservations = mergeIncomingObservationsIntoCache(
    postpartumIncoming,
    reduxEntities,
    observationsCacheRef,
  );
  if (asyncSeq !== bundleAsyncSeqRef.current) {
    return;
  }
  await processObservations(allObservations, asyncSeq);
  if (asyncSeq !== bundleAsyncSeqRef.current) {
    return;
  }
}

export type LaborEncounterObservationBundleDeps = {
  clientId: string;
  relatedPatientId: string | null;
  stages: Record<LaborStage, Observation>;
  dispatch: AppDispatch;
  getState: () => RootState;
  observationsCacheRef: RefCell<Map<string, Observation>>;
  motherCblLastAppliedMetaMsRef: RefCell<number>;
  setLiveCumulativeBloodLoss: (value: number) => void;
  processObservations: (observations: Observation[], asyncSeq: number) => Promise<void>;
  /** Injected for tests; production passes `() => fetchLaborStageObservations(medplum, clientId, encounterId)`. */
  fetchLaborStageObservationsBundle: () => Promise<Observation[]>;
  bundleAsyncSeqRef: RefCell<number>;
};

/**
 * Applies one Medplum subscription notification bundle for the labor encounter live stream.
 * Callers must pass `asyncSeq` from `++bundleAsyncSeqRef.current` before awaiting this function.
 */
export async function runLaborEncounterObservationBundle(
  resources: Observation[],
  asyncSeq: number,
  deps: LaborEncounterObservationBundleDeps,
): Promise<void> {
  if (resources.length === 0) return;

  const {
    clientId,
    relatedPatientId,
    stages,
    dispatch,
    getState,
    observationsCacheRef,
    motherCblLastAppliedMetaMsRef,
    setLiveCumulativeBloodLoss,
    processObservations,
    fetchLaborStageObservationsBundle,
    bundleAsyncSeqRef,
  } = deps;

  applyBirthObservationUpdatesFromBundle(resources, clientId, getState, dispatch);

  const motherCumulativeBloodLossObservations = resources.filter((observation) =>
    isMotherCumulativeBloodLossObservation(observation, clientId),
  );
  if (motherCumulativeBloodLossObservations.length > 0) {
    const latest = [...motherCumulativeBloodLossObservations].sort(
      (a, b) => observationMetaTimeMs(b) - observationMetaTimeMs(a),
    )[0];
    applyMotherCumulativeBloodLossIfNewer(
      latest,
      motherCblLastAppliedMetaMsRef,
      setLiveCumulativeBloodLoss,
    );
  }

  const laborStageResources = resources.filter(
    (observation) =>
      observation.code?.coding?.some((codingEntry) => codingEntry.code === LOINCCodes.LaborStage) &&
      observationSubjectId(observation) === clientId,
  );

  let shouldRefetchLaborStagesFromServer = motherCumulativeBloodLossObservations.length > 0;

  if (laborStageResources.length > 0) {
    const hasNewObservations = laborStageResources.some((observation) => {
      const answerCode = getLaborStageAnswerCode(observation) as LaborStage | undefined;
      if (!answerCode) return true;
      return (
        !stages[answerCode] ||
        observation.meta?.lastUpdated !== stages[answerCode]?.meta?.lastUpdated
      );
    });

    if (hasNewObservations) {
      dispatch(
        loadLaborStages(
          dedupeLaborStagesPreservingAmendPreviews(
            laborStageResources,
            selectLaborStageEntities(getState()),
          ),
        ),
      );
      shouldRefetchLaborStagesFromServer = true;
    }
  }

  if (shouldRefetchLaborStagesFromServer) {
    try {
      const allObservations = await fetchLaborStageObservationsBundle();
      if (asyncSeq === bundleAsyncSeqRef.current) {
        dispatch(
          loadLaborStages(
            dedupeLaborStagesPreservingAmendPreviews(
              allObservations,
              selectLaborStageEntities(getState()),
            ),
          ),
        );
      }
    } catch (error) {
      console.error("Error refreshing labor stage data:", error);
    }
  }

  await mergePostpartumObservationsFromLiveBundle(
    resources,
    asyncSeq,
    clientId,
    relatedPatientId,
    getState,
    observationsCacheRef,
    processObservations,
    bundleAsyncSeqRef,
  );
  if (asyncSeq !== bundleAsyncSeqRef.current) {
    return;
  }
}
